import React from "react";

import { getSpecConfig } from "../../../../commons/specs";
import { ActiveFilters, FilterKey, getTypedFilterConfigEntries } from "../../../../commons/specs/filters";
import { ProductType } from "../../../../commons/specs/product";
import config from "../../config";
import { useMediaQuery } from "../../libs/hooks/use-media-query";
import { useBrands } from "../../libs/queries/use-brands";
import useEnv from "../../libs/queries/use-env";
import useProductFilter from "../../libs/queries/use-product-filter";
import { useSettings } from "../../libs/queries/use-settings";
import useVeloconnectEndpoints from "../../libs/queries/use-veloconnect-endpoints";
import FilterModalPartial from "../ProductFinder/FilterModalPartial";
import { useTrackingContext } from "../utils/tracking-context";
import { VeloconnectEndpointsProvider } from "../utils/veloconnect-endpoints-context";

import ModalContainerWithStatePartial from "./ModalContainerWithStatePartial";

type RenderProps = {
  isOpen: boolean;
  open: () => void;
};

type Props<Type extends ProductType = ProductType> = {
  filterKey: FilterKey<Type>;
  activeFilters: ActiveFilters<Type>;
  onFilterChange: (activeFilters: ActiveFilters<Type>) => void;
  children: (renderProps: RenderProps) => React.ReactNode;
  disableDispatchOnAvailabilityLocationChange?: boolean;
};

const StandaloneFilterModal = <Type extends ProductType = ProductType>({
  filterKey,
  children,
  activeFilters,
  onFilterChange,
  disableDispatchOnAvailabilityLocationChange
}: Props<Type>) => {
  const env = useEnv<Type>();
  const settings = useSettings();
  const tracking = useTrackingContext();
  const breakpoints = useMediaQuery();
  const brands = useBrands();
  const veloconnectEndpoints = useVeloconnectEndpoints();
  const productFilter = useProductFilter<Type>({
    productType: env.productType,
    assortmentFilterSettings: settings.assortmentFilterSettings
  });

  const filterKeyString = String(filterKey);
  const specConfig = getSpecConfig(env.productType);
  const filterConfigEntries = getTypedFilterConfigEntries(specConfig.filterConfig);
  const filterConfig = filterConfigEntries.filter(([key]) => key === filterKey)[0][1];

  const trackModalInteraction = (isOpen: boolean) => {
    if (isOpen) {
      tracking.mixpanel?.trackFilterModalOpened(filterKeyString);
    } else {
      tracking.mixpanel?.trackFilterModalClosed(filterKeyString);
    }
  };

  return (
    <VeloconnectEndpointsProvider endpoints={veloconnectEndpoints}>
      <ModalContainerWithStatePartial
        key={filterKeyString}
        position="center"
        animationDuration={config.shared.transitionsDurations.fast}
        hideBackground={breakpoints.xl}
        onOpenChange={trackModalInteraction}
        modal={(isOpen, close) => (
          <FilterModalPartial<Type>
            isOpen={isOpen}
            isFetching={productFilter.query.isFetching}
            productType={env.productType}
            filterKey={filterKey}
            filterConfig={filterConfig}
            filterValues={productFilter.query.data ?? {}}
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            highlightedBrandKeys={brands.highlightedKeys}
            onClose={close}
            disableDispatchOnAvailabilityLocationChange={disableDispatchOnAvailabilityLocationChange}
          />
        )}
      >
        {(isOpen, open) => children({ isOpen, open })}
      </ModalContainerWithStatePartial>
    </VeloconnectEndpointsProvider>
  );
};

export default StandaloneFilterModal;
